import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { AuthService } from '../../../core/services/auth.service';
import { SiteService } from '../../../core/services/site.service';
import { NotificationService } from '../../../core/services/notification.service';
import { SettingsService } from '../../../core/services/settings.service';
import { SiteSummary, User, UserNotification } from '../../../core/models';
import { environment } from '../../../../environments/environment';
import { UserGuideDialogComponent } from '../../components/user-guide-dialog/user-guide-dialog.component';
import { POS_SHORTCUTS } from '../../data/pos-shortcuts';
import {
  PageHeaderAction,
  PageHeaderService,
} from '../../services/page-header.service';

type NotificationTone = 'info' | 'success' | 'warning' | 'danger';
type NotificationCategory =
  | 'stock'
  | 'supplier'
  | 'product'
  | 'system'
  | 'general'
  | 'sales';

interface NotificationActionButton {
  label: string;
  route?: string;
  variant?: 'primary' | 'secondary';
}

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  timeAgo: string;
  category: NotificationCategory;
  isRead: boolean;
  link?: string;
  metadata?: string;
  fileName?: string;
  fileSize?: string;
  actor?: {
    name: string;
    role?: string;
    avatarUrl?: string;
  };
  chip?: {
    label: string;
    tone: NotificationTone;
  };
  actions?: NotificationActionButton[];
}

interface NotificationGroup {
  title: string;
  subtitle: string;
  accentIcon: string;
  items: NotificationItem[];
}

interface SidebarLinkConfig {
  label: string;
  icon: string;
  route: string;
  title: string;
  exact?: boolean;
  // Extra exact URLs (besides `route`) that should mark this link active.
  // Useful when one menu entry covers several sibling routes (e.g. a resolver).
  activeMatch?: string[];
  permissionsAny?: string[];
  permissionsAll?: string[];
  roles?: string[];
  excludeRoles?: string[];
}

interface SidebarSectionConfig {
  label: string;
  links: SidebarLinkConfig[];
}

@Component({
  selector: 'app-main-layout',
  standalone: false,
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  // Application version
  version = '1.0.0';
  businessName = environment.appName;

  // Sidebar state
  sidebarCollapsed = false;
  navSearchQuery = '';

  // User information displayed in the header
  userName = 'User';
  userRole = '';
  userInitials = '?';

  // Keyboard shortcuts help panel
  shortcuts = POS_SHORTCUTS;
  shortcutsOpen = false;

  // Notifications
  notificationsOpen = false;
  notificationsLoading = false;
  notificationsError: string | null = null;
  lastNotificationsRefresh: Date | null = null;
  notificationGroups: NotificationGroup[] = [];
  notificationUnreadCount = 0;

  get notificationCount(): number {
    return this.notificationUnreadCount;
  }


  visiblePrimaryLinks: SidebarLinkConfig[] = [];
  visibleSidebarSections: SidebarSectionConfig[] = [];

  get filteredPrimaryLinks(): SidebarLinkConfig[] {
    const q = this.navSearchQuery.trim().toLowerCase();
    if (!q) return this.visiblePrimaryLinks;
    return this.visiblePrimaryLinks.filter((l) =>
      l.label.toLowerCase().includes(q),
    );
  }

  get filteredSidebarSections(): SidebarSectionConfig[] {
    const q = this.navSearchQuery.trim().toLowerCase();
    if (!q) return this.visibleSidebarSections;
    return this.visibleSidebarSections
      .map((s) => ({
        ...s,
        links: s.links.filter((l) => l.label.toLowerCase().includes(q)),
      }))
      .filter((s) => s.links.length > 0);
  }

  private readonly siteService = inject(SiteService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly dialog = inject(MatDialog);
  readonly pageHeader = inject(PageHeaderService);

  trackByAction(index: number, action: PageHeaderAction): string {
    return action.id ?? action.icon ?? String(index);
  }

  actionLabel(action: PageHeaderAction): string {
    return typeof action.label === 'function'
      ? action.label()
      : (action.label ?? '');
  }

  isActionDisabled(action: PageHeaderAction): boolean {
    return typeof action.disabled === 'function'
      ? action.disabled()
      : !!action.disabled;
  }

  isActionHidden(action: PageHeaderAction): boolean {
    return typeof action.hidden === 'function'
      ? action.hidden()
      : !!action.hidden;
  }

  // Site context — the switcher only lists sites the user is assigned to.
  siteOptions$: Observable<SiteSummary[]> = this.siteService.switchableSites$;
  activeSite$: Observable<SiteSummary | null> = this.siteService.activeSite$;
  canSwitchSites$: Observable<boolean> = this.siteService.canSwitchSites$;
  isLoadingSites$: Observable<boolean> = this.siteService.isLoading$;

  // Menu triggers to prevent both menus from being open simultaneously
  @ViewChild('siteSwitcherMenuTrigger') siteSwitcherMenuTrigger?: MatMenuTrigger;
  @ViewChild('userMenuTrigger') userMenuTrigger?: MatMenuTrigger;
  @ViewChild('navSearchInput') navSearchInput?: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  private permissionSet = new Set<string>();
  private currentRoleSlug: string | null = null;
  private readonly dashboardLinks: SidebarLinkConfig[] = [
    {
      label: 'Dashboard Overview',
      icon: 'dashboard',
      route: '/dashboard',
      title: 'Dashboard Overview',
      exact: true,
      // KPIs mirror sales-report data; gate to match the backend dashboard endpoints.
      permissionsAny: ['report.sales'],
    },
  ];

  private readonly sidebarSections: SidebarSectionConfig[] = [
    {
      label: 'Sales & CRM',
      links: [
        {
          label: 'POS Checkout',
          icon: 'point_of_sale',
          route: '/pos',
          title: 'POS Checkout',
          // `/pos` resolves to checkout; highlight for that route too.
          activeMatch: ['/pos/checkout'],
          permissionsAny: ['sale.read', 'sale.create'],        },
        {
          label: 'Transactions',
          icon: 'receipt_long',
          route: '/pos/transactions',
          title: 'Transactions',
          permissionsAny: ['sale.read'],        },
        {
          label: 'Customers',
          icon: 'people',
          route: '/customers',
          title: 'Customer Directory',
          permissionsAny: ['customer.read', 'customer.loyalty.read'],        },
      ],
    },
    {
      label: 'Catalog',
      links: [
        {
          label: 'Products',
          icon: 'inventory_2',
          route: '/products',
          title: 'Products',
          permissionsAny: ['product.read'],        },
        {
          label: 'Categories',
          icon: 'category',
          route: '/products/categories',
          title: 'Categories',
          permissionsAny: ['product.read'],        },
        {
          label: 'Brands',
          icon: 'sell',
          route: '/products/brands',
          title: 'Brands',
          permissionsAny: ['product.read'],        },
      ],
    },
    {
      label: 'Inventory',
      links: [
        // Inventory Dashboard link removed
        // {
        //   label: 'Stock Adjustments',
        //   icon: 'tune',
        //   route: '/inventory/adjustments',
        //   title: 'Stock Adjustments',
        //   permissionsAny: ['inventory.adjust', 'inventory.update'],
        //   requiredModules: ['inventory'],
        // },
        // {
        //   label: 'Stock Transfers',
        //   icon: 'compare_arrows',
        //   route: '/inventory/transfers',
        //   title: 'Stock Transfers',
        //   permissionsAny: ['inventory.update'],
        //   requiredModules: ['inventory'],
        // },
        {
          label: 'Suppliers',
          icon: 'local_shipping',
          route: '/inventory/suppliers',
          title: 'Suppliers',
          permissionsAny: ['inventory.read'],        },
        {
          label: 'Purchase Orders',
          icon: 'assignment',
          route: '/inventory/purchase-orders',
          title: 'Purchase Orders',
          permissionsAny: ['inventory.read'],        },
        {
          label: 'Goods Received Notes',
          icon: 'assignment_turned_in',
          route: '/inventory/grn',
          title: 'Goods Received Notes',
          permissionsAny: ['inventory.read'],        },
        {
          label: 'Purchase Invoices',
          icon: 'receipt_long',
          route: '/inventory/purchase-invoices',
          title: 'Purchase Invoices',
          permissionsAny: ['inventory.read'],        },
        {
          label: 'Stock Alerts',
          icon: 'warning_amber',
          route: '/inventory/alerts',
          title: 'Stock Alerts',
          permissionsAny: ['inventory.read'],        },
      ],
    },
    {
      label: 'Reports & Analytics',
      links: [
        {
          label: 'Sales Reports',
          icon: 'trending_up',
          route: '/reports/sales',
          title: 'Sales Reports',
          permissionsAny: ['report.sales'],        },
        {
          label: 'Inventory Reports',
          icon: 'fact_check',
          route: '/reports/inventory',
          title: 'Inventory Reports',
          permissionsAny: ['report.inventory'],        },
        {
          label: 'Customer Reports',
          icon: 'groups',
          route: '/reports/customers',
          title: 'Customer Reports',
          permissionsAny: ['report.customers'],        },
        {
          label: 'Product Performance',
          icon: 'insights',
          route: '/reports/products',
          title: 'Product Performance',
          permissionsAny: ['report.sales', 'report.inventory'],        },
      ],
    },
    {
      label: 'Administration',
      links: [
        {
          label: 'Tenants',
          icon: 'business',
          route: '/admin/tenants',
          title: 'Tenants Management',
          // Tenant management is Optwaves-dev-team only; matches the route's SuperAdminGuard.
          roles: ['super_admin'],
        },
        {
          label: 'Sites',
          icon: 'store',
          route: '/admin/sites',
          title: 'Sites Management',
          permissionsAny: ['site.read'],
        },
        {
          label: 'User Management',
          icon: 'group',
          route: '/users',
          title: 'User Management',
          permissionsAny: ['user.read', 'user.management'],
        },
        {
          label: 'Settings',
          icon: 'settings',
          route: '/settings',
          title: 'Settings',
          permissionsAny: ['settings.read', 'settings.update'],
        },
        {
          label: 'Receipt Designer',
          icon: 'receipt_long',
          route: '/settings/receipt-designer',
          title: 'Receipt Designer',
          permissionsAny: ['receipt.read', 'receipt.update'],
        },
        {
          label: 'Roles & Permissions',
          icon: 'admin_panel_settings',
          route: '/roles',
          title: 'Roles & Permissions',
          permissionsAny: ['role.read', 'role.create', 'role.update'],
        },
        {
          label: 'Audit Logs',
          icon: 'history',
          route: '/admin/audit-logs',
          title: 'Audit Logs',
          permissionsAny: ['audit.read'],
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.updateUserDisplay(this.authService.getCurrentUserValue());

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => this.updateUserDisplay(user));

    this.observeBusinessName();
    this.settingsService
      .loadSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Prefetch notification data so the badge stays accurate
    this.loadNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveSite(siteId: string): void {
    this.siteService.setActiveSite(siteId);

    // Reload the current route to refresh data for the new site
    const currentUrl = this.router.url;
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => {
        this.router.navigate([currentUrl]);
      });
  }

  onSiteSwitcherMenuOpened(): void {
    this.userMenuTrigger?.closeMenu();
  }

  onUserMenuOpened(): void {
    this.siteSwitcherMenuTrigger?.closeMenu();
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  openSearch(): void {
    // Implement search functionality
    console.log('Search clicked');
  }

  openUserGuide(): void {
    this.dialog.open(UserGuideDialogComponent, {
      maxWidth: '1400px',
      height: '85vh',
      panelClass: 'user-guide-dialog-container',
      autoFocus: false,
    });
  }

  toggleShortcutsPanel(): void {
    this.shortcutsOpen = !this.shortcutsOpen;
  }

  closeShortcutsPanel(): void {
    this.shortcutsOpen = false;
  }

  toggleNotificationsPanel(): void {
    this.notificationsOpen = !this.notificationsOpen;

    if (
      this.notificationsOpen &&
      !this.notificationsLoading &&
      !this.notificationGroups.length
    ) {
      this.loadNotifications();
    }
  }

  closeNotificationsPanel(): void {
    this.notificationsOpen = false;
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.shortcutsOpen) {
      this.closeShortcutsPanel();
    }
    if (this.notificationsOpen) {
      this.closeNotificationsPanel();
    }
    if (this.navSearchQuery) {
      this.navSearchQuery = '';
      this.navSearchInput?.nativeElement.blur();
    }
  }

  @HostListener('document:keydown.control.k', ['$event'])
  handleNavSearch(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.sidebarCollapsed) {
      this.sidebarCollapsed = false;
    }
    setTimeout(() => this.navSearchInput?.nativeElement.focus(), 50);
  }

  clearNavSearch(): void {
    this.navSearchQuery = '';
    this.navSearchInput?.nativeElement.focus();
  }

  markAllNotificationsAsRead(): void {
    if (this.notificationUnreadCount === 0) {
      return;
    }

    this.notificationService
      .markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationGroups.forEach((group) =>
            group.items.forEach((item) => {
              item.isRead = true;
            }),
          );
          this.notificationUnreadCount = 0;
        },
        error: (error) => {
          console.error('Error marking notifications as read:', error);
        },
      });
  }

  openNotificationDetail(item: NotificationItem): void {
    this.markNotificationAsRead(item.id);

    const targetRoute = item.link || '/dashboard';
    this.router
      .navigateByUrl(targetRoute)
      .finally(() => this.closeNotificationsPanel());
  }

  handleNotificationAction(
    event: MouseEvent,
    action: NotificationActionButton,
    item: NotificationItem,
  ): void {
    event.stopPropagation();

    this.markNotificationAsRead(item.id);

    if (action.route) {
      this.router
        .navigateByUrl(action.route)
        .finally(() => this.closeNotificationsPanel());
      return;
    }

    this.closeNotificationsPanel();
  }

  viewAllNotifications(): void {
    this.router
      .navigate(['/dashboard'])
      .finally(() => this.closeNotificationsPanel());
  }

  refreshNotifications(): void {
    if (this.notificationsLoading) {
      return;
    }

    this.loadNotifications();
  }

  getAvatarInitials(name?: string): string {
    if (!name) {
      return 'PP';
    }

    const segments = name.trim().split(/\s+/);

    if (segments.length === 1) {
      return segments[0].substring(0, 2).toUpperCase();
    }

    const first = segments[0].charAt(0);
    const last = segments[segments.length - 1].charAt(0);
    return `${first}${last}`.toUpperCase();
  }

  trackByNotification(_: number, item: NotificationItem): string {
    return item.id;
  }

  countUnread(items: NotificationItem[]): number {
    return items.reduce(
      (total, current) => total + (current.isRead ? 0 : 1),
      0,
    );
  }

  logout(): void {
    this.authService
      .logout()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/auth/login']);
        },
        error: () => {
          this.authService.forceLogout();
        },
      });
  }

  isLinkActive(link: SidebarLinkConfig): boolean {
    const url = this.router.url.split(/[?#]/)[0];

    if (link.activeMatch?.length) {
      return url === link.route || link.activeMatch.some((match) => url === match);
    }

    if (link.exact === false) {
      return url === link.route || url.startsWith(`${link.route}/`);
    }

    return url === link.route;
  }

  private updateUserDisplay(user: User | null): void {
    if (!user) {
      this.userName = 'User';
      this.userRole = '';
      this.userInitials = '?';
      this.currentRoleSlug = null;
      this.permissionSet = new Set();
      this.recomputeNavigation();
      return;
    }

    const displayName = this.resolveUserName(user);

    this.userName = displayName;
    this.userRole = user.role?.name ?? user.role?.slug ?? '';
    this.userInitials = this.buildInitials(displayName, user.email);
    this.currentRoleSlug = user.role?.slug ?? null;
    this.permissionSet = new Set(user.permissions ?? []);
    this.recomputeNavigation();
  }

  private recomputeNavigation(): void {
    const links = this.dashboardLinks.filter((link) =>
      this.canAccessLink(link),
    );
    this.visiblePrimaryLinks = links;

    this.visibleSidebarSections = this.sidebarSections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => this.canAccessLink(link)),
      }))
      .filter((section) => section.links.length > 0);
  }

  private canAccessLink(link: SidebarLinkConfig): boolean {
    const isSuperAdmin = this.currentRoleSlug === 'super_admin';

    if (
      link.excludeRoles?.length &&
      this.currentRoleSlug &&
      link.excludeRoles.includes(this.currentRoleSlug)
    ) {
      return false;
    }

    if (
      link.roles?.length &&
      !isSuperAdmin &&
      (!this.currentRoleSlug || !link.roles.includes(this.currentRoleSlug))
    ) {
      return false;
    }

    if (
      link.permissionsAll?.length &&
      !isSuperAdmin &&
      !link.permissionsAll.every((permission) =>
        this.permissionSet.has(permission),
      )
    ) {
      return false;
    }

    if (
      link.permissionsAny?.length &&
      !isSuperAdmin &&
      !link.permissionsAny.some((permission) =>
        this.permissionSet.has(permission),
      )
    ) {
      return false;
    }

    return true;
  }

  private resolveUserName(user: User): string {
    const fullName = user.fullName?.trim();
    if (fullName) {
      return fullName;
    }

    const combinedName = [user.firstName, user.lastName]
      .filter(Boolean)
      .map((part) => part.trim())
      .join(' ')
      .trim();

    if (combinedName) {
      return combinedName;
    }

    return user.email;
  }

  private buildInitials(nameSource: string, fallbackEmail: string): string {
    const source = nameSource || fallbackEmail;
    const segments = source.split(/\s+/).filter(Boolean);

    if (segments.length === 0 && fallbackEmail) {
      return fallbackEmail.substring(0, 2).toUpperCase();
    }

    if (segments.length === 1) {
      return segments[0].substring(0, 2).toUpperCase();
    }

    const first = segments[0]?.charAt(0) ?? '';
    const last = segments[segments.length - 1]?.charAt(0) ?? '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || '?';
  }

  private loadNotifications(): void {
    this.notificationsLoading = true;
    this.notificationsError = null;

    forkJoin({
      list: this.notificationService.getNotifications({
        status: 'all',
        perPage: 10,
      }),
      unread: this.notificationService.getUnreadCount(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ list, unread }) => {
          const notifications = list?.data ?? [];
          this.notificationGroups =
            this.buildNotificationGroupsFromBackend(notifications);
          this.notificationUnreadCount = unread ?? 0;
          this.notificationsLoading = false;
          this.lastNotificationsRefresh = new Date();
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
          this.notificationsLoading = false;
          this.notificationsError = 'Unable to load notifications right now.';
          this.notificationGroups = [];
        },
      });
  }

  private observeBusinessName(): void {
    this.settingsService.settings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const updatedName = state.general?.businessName?.trim();
        this.businessName = updatedName?.length
          ? updatedName
          : environment.appName;
      });
  }

  private buildNotificationGroupsFromBackend(
    notifications: UserNotification[],
  ): NotificationGroup[] {
    const grouped = new Map<NotificationCategory, NotificationItem[]>();

    notifications.forEach((notification) => {
      const mapped = this.mapBackendNotification(notification);
      const category = mapped.category;
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)?.push(mapped);
    });

    if (!grouped.size) {
      return [];
    }

    return Array.from(grouped.entries()).map(([category, items]) => {
      const presentation = this.getCategoryPresentation(category);
      return {
        title: presentation.title,
        subtitle: presentation.subtitle,
        accentIcon: presentation.accentIcon,
        items,
      };
    });
  }

  private getCategoryPresentation(category: NotificationCategory): {
    title: string;
    subtitle: string;
    accentIcon: string;
  } {
    const catalog: Record<
      NotificationCategory,
      { title: string; subtitle: string; accentIcon: string }
    > = {
      stock: {
        title: 'Inventory alerts',
        subtitle: 'Live stock and replenishment signals',
        accentIcon: 'inventory_2',
      },
      supplier: {
        title: 'Suppliers & transfers',
        subtitle: 'Keep vendor and routing requests moving',
        accentIcon: 'local_shipping',
      },
      product: {
        title: 'Product updates',
        subtitle: 'Catalog, merchandising, and assets',
        accentIcon: 'category',
      },
      system: {
        title: 'System activity',
        subtitle: 'Platform, security, and account notices',
        accentIcon: 'shield',
      },
      sales: {
        title: 'Sales & payments',
        subtitle: 'POS, settlement, and checkout alerts',
        accentIcon: 'point_of_sale',
      },
      general: {
        title: 'Workspace updates',
        subtitle: 'Latest activity across your sites',
        accentIcon: 'notifications',
      },
    };

    return catalog[category] ?? catalog.general;
  }

  private mapBackendNotification(
    notification: UserNotification,
  ): NotificationItem {
    return {
      id: notification.id,
      title: notification.title || 'Notification',
      message: notification.body || 'No additional details provided.',
      timeAgo: this.formatTimeAgo(notification.createdAt ?? undefined),
      category: this.resolveNotificationCategory(notification.category),
      isRead: notification.isRead,
      link: this.resolveNotificationLink(notification),
      metadata: this.buildNotificationMetadata(notification),
      actor: this.buildNotificationActor(notification),
      chip: this.buildNotificationChip(notification),
    };
  }

  private resolveNotificationCategory(
    category?: string | null,
  ): NotificationCategory {
    if (!category) {
      return 'general';
    }

    const normalized = category.trim().toLowerCase();

    if (normalized.includes('stock') || normalized.includes('inventory')) {
      return 'stock';
    }

    if (
      normalized.includes('supplier') ||
      normalized.includes('transfer') ||
      normalized.includes('purchase')
    ) {
      return 'supplier';
    }

    if (normalized.includes('product') || normalized.includes('catalog')) {
      return 'product';
    }

    if (
      normalized.includes('system') ||
      normalized.includes('security') ||
      normalized.includes('platform')
    ) {
      return 'system';
    }

    if (
      normalized.includes('sale') ||
      normalized.includes('payment') ||
      normalized.includes('pos')
    ) {
      return 'sales';
    }

    return 'general';
  }

  private buildNotificationActor(
    notification: UserNotification,
  ): NotificationItem['actor'] | undefined {
    const payload = this.normalizeNotificationData(notification);
    const actorName =
      this.getString(payload?.['actorName']) ??
      this.getString(payload?.['user']) ??
      this.getString(payload?.['owner']);
    const actorRole =
      this.getString(payload?.['actorRole']) ??
      this.getString(payload?.['role']);
    const avatarUrl =
      this.getString(payload?.['actorAvatar']) ??
      this.getString(payload?.['avatar']);

    if (!actorName && !actorRole && !avatarUrl) {
      return undefined;
    }

    return {
      name: actorName ?? 'Optwaves POS',
      role: actorRole ?? undefined,
      avatarUrl: avatarUrl ?? undefined,
    };
  }

  private buildNotificationChip(
    notification: UserNotification,
  ): NotificationItem['chip'] | undefined {
    const severity = this.getString(notification.severity ?? undefined);
    if (!severity) {
      return undefined;
    }

    const label = this.toTitleCase(severity.replace(/_/g, ' '));
    return {
      label,
      tone: this.mapSeverityToTone(severity),
    };
  }

  private buildNotificationMetadata(
    notification: UserNotification,
  ): string | undefined {
    const payload = this.normalizeNotificationData(notification);

    if (!payload) {
      return undefined;
    }

    const parts: string[] = [];
    const entity =
      this.getString(payload['entityName']) ??
      this.getString(payload['entity']) ??
      this.getString(payload['target']);
    const reference =
      this.getString(payload['reference']) ??
      this.getString(payload['code']) ??
      this.getString(payload['document']);
    const location =
      this.getString(payload['locationName']) ??
      this.getString(payload['location']) ??
      this.getString(payload['site']);

    if (entity) {
      parts.push(entity);
    }

    if (reference) {
      parts.push(reference);
    }

    if (location) {
      parts.push(location);
    }

    if (!parts.length && notification.category) {
      parts.push(this.formatCategoryLabel(notification.category));
    }

    return parts.length ? parts.join(' • ') : undefined;
  }

  private resolveNotificationLink(
    notification: UserNotification,
  ): string | undefined {
    const payload = this.normalizeNotificationData(notification);
    const destination =
      this.getString(payload?.['link']) ??
      this.getString(payload?.['route']) ??
      this.getString(payload?.['url']);
    return destination ?? undefined;
  }

  private normalizeNotificationData(
    notification: UserNotification,
  ): Record<string, unknown> | null {
    if (!notification.data || typeof notification.data !== 'object') {
      return null;
    }

    return notification.data as Record<string, unknown>;
  }

  private markNotificationAsRead(notificationId: string): void {
    const target = this.findNotificationItem(notificationId);
    if (!target || target.isRead) {
      return;
    }

    target.isRead = true;
    this.notificationUnreadCount = Math.max(
      this.notificationUnreadCount - 1,
      0,
    );

    this.notificationService
      .markAsRead(notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          console.error('Error marking notification as read:', error);
          target.isRead = false;
          this.notificationUnreadCount += 1;
        },
      });
  }

  private findNotificationItem(
    notificationId: string,
  ): NotificationItem | undefined {
    for (const group of this.notificationGroups) {
      const match = group.items.find((item) => item.id === notificationId);
      if (match) {
        return match;
      }
    }

    return undefined;
  }

  private mapSeverityToTone(severity?: string | null): NotificationTone {
    const normalized = severity?.toLowerCase() ?? '';

    switch (normalized) {
      case 'critical':
      case 'danger':
        return 'danger';
      case 'warning':
      case 'high':
        return 'warning';
      case 'success':
      case 'low':
        return 'success';
      default:
        return 'info';
    }
  }

  private toTitleCase(value: string): string {
    return value
      .split(' ')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private getString(value: unknown): string | undefined {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    }

    return undefined;
  }

  private formatCategoryLabel(value: string): string {
    return value
      .split(/[\s_-]+/)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private formatTimeAgo(value: Date | string | undefined): string {
    if (!value) {
      return 'Just now';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Just now';
    }

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
      return 'Just now';
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      const unit = diffHours === 1 ? 'hr' : 'hrs';
      return `${diffHours} ${unit} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {
      const unit = diffDays === 1 ? 'day' : 'days';
      return `${diffDays} ${unit} ago`;
    }

    return date.toLocaleDateString();
  }
}
